import { sortAsc } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useSchoolModules } from '../hooks/useSchoolModules';
import {
  PlusIcon, XMarkIcon, PencilSquareIcon, FunnelIcon,
} from '@heroicons/react/24/outline';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

/* ── Category → subcategory map ───────────────────────────────────────── */
const SUB_OPTIONS = {
  electricity: ['Monthly Bill','Wiring Repair','Meter Replacement','New Connection','Transformer','Other'],
  generator:   ['Fuel Refill','Engine Oil Change','Filter Change','Belt Replacement','Coolant','Full Service','Repair','Spare Parts','Other'],
  water:       ['Monthly Bill','Tank Cleaning','Pipe Repair','Pump Repair','New Pipe Installation','Other'],
  rent:        ['Monthly Rent','Security Deposit','Advance','Other'],
  salaries:    ['Teaching Staff','Non-Teaching Staff','Security','Cleaning Staff','Driver','Other'],
  stationery:  ['Paper/Registers','Pens/Pencils','Printer Ink','Whiteboard Markers','Other'],
  maintenance: ['Plumbing','Electrical Work','Painting','Carpentry','Cleaning','Ceiling/Roof','Floor','Windows/Doors','AC Service','Other'],
  repair:      ['Furniture','Electronics','Vehicle','Building','Other'],
  transport:   ['Fuel','Vehicle Repair','Insurance','Registration/Tax','Driver Salary','Other'],
  equipment:   ['Purchase','Repair','Calibration','Other'],
  furniture:   ['Purchase','Repair','Polish/Refinish','Other'],
  events:      ['Sports Day','Annual Function','Exam Fee','Prize Distribution','Trip','Other'],
  miscellaneous:['General Expense','Refreshments','Printing','Medical','Other'],
};

const CATEGORIES = Object.keys(SUB_OPTIONS);

const CAT_COLORS = {
  electricity:'#f59e0b', generator:'#ef4444', water:'#3b6ef8', rent:'#8b5cf6',
  salaries:'#10c98e', stationery:'#6366f1', maintenance:'#f97316', repair:'#ec4899',
  transport:'#14b8a6', equipment:'#0ea5e9', furniture:'#84cc16', events:'#a855f7', miscellaneous:'#9094a8',
};
const CAT_BG = {
  electricity:'#fffbeb', generator:'#fef2f2', water:'#eef2ff', rent:'#f3f0ff',
  salaries:'#ecfdf5', stationery:'#eef2ff', maintenance:'#fff7ed', repair:'#fdf2f8',
  transport:'#f0fdfa', equipment:'#f0f9ff', furniture:'#f7fee7', events:'#faf5ff', miscellaneous:'#f0f2f7',
};

const CAT_ICONS = {
  electricity:'⚡', generator:'🔧', water:'💧', rent:'🏠', salaries:'👥',
  stationery:'📝', maintenance:'🛠️', repair:'🔩', transport:'🚌',
  equipment:'⚙️', furniture:'🪑', events:'🎉', miscellaneous:'📦',
};

const fmtMoney = (n) => {
  if (!n) return 'Rs. 0';
  if (n >= 1000000) return `Rs. ${(n/1000000).toFixed(1)}M`;
  if (n >= 1000)    return `Rs. ${(n/1000).toFixed(0)}K`;
  return `Rs. ${n.toLocaleString('en-PK')}`;
};
const fmtDate = (d) => new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'});
const nowMonth = () => new Date().toISOString().slice(0,7);
const EMPTY_FORM = { title:'', category:'electricity', subcategory:'', amount:'', date:new Date().toISOString().slice(0,10), description:'', vendor:'', receiptNo:'', paymentMethod:'cash', meterReading:'', previousReading:'', module:'school' };

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ex * { box-sizing:border-box; }
  .ex { font-family:'Sora',sans-serif; color:#1a1d2e; animation:exFade .4s ease both; }
  @keyframes exFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  /* Header */
  .ex-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .ex-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .ex-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }

  /* Buttons */
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; white-space:nowrap; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover  { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); transform:translateY(-1px); }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover   { background:#ef4444; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }

  /* Stat cards */
  .stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.2rem; border:1px solid #e8ebf4; }
  .sc-icon { width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:.7rem; font-size:1rem; }
  .sc-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sc-value { font-size:1.3rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; line-height:1.2; margin-top:2px; }
  .sc-sub   { font-size:.68rem; color:#b0b5cc; margin-top:3px; }

  /* Charts */
  .charts-row { display:grid; grid-template-columns:2fr 1fr; gap:1rem; margin-bottom:1.4rem; }
  .chart-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; }
  .chart-title { font-size:.86rem; font-weight:600; color:#1a1d2e; margin-bottom:4px; }
  .chart-sub   { font-size:.72rem; color:#9094a8; margin-bottom:1rem; }

  /* Module tabs */
  .cat-tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .cat-tab  { padding:5px 12px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.75rem; font-weight:600; cursor:pointer; transition:all .15s; color:#6b7194; white-space:nowrap; }
  .cat-tab:hover  { border-color:#3b6ef8; color:#3b6ef8; }
  .cat-tab.active { background:#3b6ef8; color:white; border-color:#3b6ef8; }

  /* Filter bar */
  .filter-bar { display:flex; gap:.6rem; flex-wrap:wrap; margin-bottom:1.2rem; align-items:center; }
  .filter-select,.filter-input { font-family:'Sora',sans-serif; font-size:.78rem; padding:7px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; }
  .filter-select:focus,.filter-input:focus { border-color:#3b6ef8; background:white; }

  /* Table */
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .ex-table { width:100%; border-collapse:collapse; }
  .ex-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .ex-table th { padding:10px 14px; text-align:center !important; font-size:.63rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .ex-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .ex-table tbody tr:last-child { border-bottom:none; }
  .ex-table tbody tr:hover { background:#fafbff; }
  .ex-table td { padding:11px 14px; font-size:.8rem; vertical-align:middle; text-align:center !important; }

  /* Mobile cards for table */
  .ex-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem; margin-bottom:.7rem; }
  .ec-top  { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:.7rem; gap:8px; }
  .ec-title{ font-size:.86rem; font-weight:700; color:#0f1123; }
  .ec-meta { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .ec-row  { display:flex; justify-content:space-between; align-items:center; padding:5px 0; border-bottom:1px solid #f5f6fb; font-size:.76rem; }
  .ec-row:last-of-type { border-bottom:none; }
  .ec-row-label { color:#9094a8; }
  .ec-actions { display:flex; gap:6px; margin-top:.8rem; padding-top:.7rem; border-top:1px solid #f0f2f8; }

  .cat-badge { display:inline-flex; align-items:center; gap:4px; padding:3px 9px; border-radius:99px; font-size:.68rem; font-weight:700; text-transform:capitalize; white-space:nowrap; }
  .sub-badge { display:inline-flex; padding:2px 7px; border-radius:99px; font-size:.65rem; font-weight:600; background:#f0f2f7; color:#6b7194; white-space:nowrap; }
  .amount-cell { font-family:'JetBrains Mono',monospace; font-size:.82rem; font-weight:700; color:#ef4444; }

  .ex-empty   { text-align:center; padding:3rem; color:#9094a8; font-size:.82rem; }
  .ex-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  /* Modal is portaled to document.body, outside .ex, so the .ex * box-sizing
     reset above never reaches it — this page has no Tailwind preflight active,
     so without this every input/select/textarea defaults to content-box and
     renders wider than 100% by its own padding+border, overflowing its column. */
  .modal-backdrop, .modal-backdrop * { box-sizing:border-box; }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:flex-start; justify-content:center; z-index:1000; padding:24px 16px; overflow-y:auto; }
  .modal { background:white; border-radius:20px; width:100%; max-width:580px; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; margin:auto; overflow:hidden; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; gap:8px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-body { padding:20px 26px 20px 22px; max-height:62vh; overflow-y:auto; overflow-x:hidden; scrollbar-gutter:stable; box-sizing:border-box; }
  .modal-foot { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; flex-wrap:wrap; }

  /* Form */
  .form-grid { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:12px; }
  .form-grid > *, .fg > * { min-width:0; }
  .form-full  { grid-column:1/-1; }
  .form-group { display:flex; flex-direction:column; gap:5px; }
  .form-label { font-size:.7rem; font-weight:700; color:#6b7194; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:#3b6ef8; background:white; }
  .form-textarea { resize:vertical; min-height:70px; }

  /* Info box */
  .info-box { background:#eef2ff; border-radius:9px; padding:8px 12px; font-size:.78rem; color:#3b6ef8; font-weight:600; margin-top:4px; }

  .custom-tooltip { background:white; border:1px solid #e8ebf4; border-radius:10px; padding:8px 12px; font-family:'Sora',sans-serif; font-size:.74rem; box-shadow:0 4px 16px rgba(0,0,0,.1); }

  /* Responsive */
  @media (max-width:900px) { .stat-grid{grid-template-columns:repeat(2,1fr);} .charts-row{grid-template-columns:1fr;} }
  @media (max-width:768px) {
    .ex-title{font-size:1.1rem;}
    .ex-table-wrap { display:none; }
    .ex-cards-wrap  { display:block; }
    .form-grid { grid-template-columns:1fr; }
    .modal-foot { justify-content:stretch; }
    .modal-foot .btn { flex:1; justify-content:center; }
    .modal-body { max-height:70vh; }
  }
  @media (min-width:769px) { .ex-cards-wrap { display:none; } .ex-table-wrap { display:block; } }
  @media (max-width:480px) { .stat-grid{grid-template-columns:1fr 1fr;} }
`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active||!payload?.length) return null;
  return (
    <div className="custom-tooltip">
      <div style={{fontWeight:700,marginBottom:4,color:'#0f1123'}}>{label}</div>
      {payload.map((p,i)=><div key={i} style={{color:'#6b7194'}}>{p.name}: <strong style={{color:'#ef4444'}}>{fmtMoney(p.value)}</strong></div>)}
    </div>
  );
};

const Expenses = () => {
  const { libraryEnabled, hostelEnabled } = useSchoolModules();
  const showModuleTabs = libraryEnabled || hostelEnabled;
  const [expenses,       setExpenses]       = useState([]);
  const [summary,        setSummary]        = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [modal,          setModal]          = useState(false);
  const [editing,        setEditing]        = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [filterMonth,    setFilterMonth]    = useState(nowMonth());
  const [filterCategory, setFilterCategory] = useState('');
  const [filterModule,   setFilterModule]   = useState('');

  useEffect(()=>{fetchAll();},[filterMonth,filterCategory,filterModule]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const params={};
      if(filterMonth)    params.month    = filterMonth;
      if(filterCategory) params.category = filterCategory;
      if(filterModule)   params.module   = filterModule;
      const [expRes,sumRes] = await Promise.all([
        api.get('/admin/expenses',{params}),
        api.get('/admin/expenses/summary'),
      ]);
      setExpenses(expRes.data||[]);
      setSummary(sumRes.data);
    } catch { toast.error('Failed to load expenses'); }
    finally  { setLoading(false); }
  };

  const openAdd  = () => { setEditing(null); setForm(EMPTY_FORM); setModal(true); };
  const openEdit = (exp) => {
    setEditing(exp);
    setForm({
      title:exp.title, category:exp.category, subcategory:exp.subcategory||'',
      amount:exp.amount, date:exp.date?.slice(0,10),
      description:exp.description||'', vendor:exp.vendor||'',
      receiptNo:exp.receiptNo||'', paymentMethod:exp.paymentMethod||'cash',
      meterReading:exp.meterReading||'', previousReading:exp.previousReading||'',
      module:exp.module||'school',
    });
    setModal(true);
  };

  const setF = (key,val) => setForm(f=>({...f,[key]:val}));

  // When category changes, reset subcategory
  const handleCatChange = (cat) => {
    setForm(f=>({...f, category:cat, subcategory:'', title: SUB_OPTIONS[cat]?.[0]||'' }));
  };

  const handleSave = async () => {
    if (!form.amount||!form.date) return toast.error('Amount and date are required');
    // Auto-set title from subcategory if empty
    const payload = {...form, title: form.title || form.subcategory || form.category};
    try {
      setSaving(true);
      if(editing){
        const{data}=await api.put(`/admin/expenses/${editing._id}`,payload);
        setExpenses(prev=>prev.map(e=>e._id===editing._id?data:e));
        toast.success('Updated');
      } else {
        const{data}=await api.post('/admin/expenses',payload);
        setExpenses(prev=>[data,...prev]);
        toast.success('Expense added');
      }
      setModal(false);
      fetchAll();
    } catch(err){ toast.error(err.response?.data?.message||'Failed to save'); }
    finally { setSaving(false); }
  };

  const totalFiltered = expenses.reduce((s,e)=>s+e.amount,0);
  const pieData = summary?.categoryBreakdown?.map(c=>({name:c._id,value:c.total,fill:CAT_COLORS[c._id]||'#9094a8'}))||[];
  const subs = SUB_OPTIONS[form.category]||[];

  return (
    <div className="ex"><style>{style}</style>

      <div className="ex-header">
        <div>
          <div className="ex-title">Expenses</div>
          <div className="ex-sub">Track school spending — utilities, salaries, maintenance & more</div>
        </div>
        <button className="btn btn-blue" onClick={openAdd}><PlusIcon/>Add Expense</button>
      </div>

      {/* Stat Cards */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="sc-icon" style={{background:'#fef2f2'}}>💸</div>
          <div className="sc-label">This Month</div>
          <div className="sc-value" style={{fontSize:'.95rem'}}>{fmtMoney(summary?.thisMonth?.total)}</div>
          <div className="sc-sub">{summary?.thisMonth?.count||0} expenses</div>
        </div>
        <div className="stat-card">
          <div className="sc-icon" style={{background:'#fffbeb'}}>📊</div>
          <div className="sc-label">This Year</div>
          <div className="sc-value" style={{fontSize:'.95rem'}}>{fmtMoney(summary?.thisYear?.total)}</div>
          <div className="sc-sub">total spending</div>
        </div>
        <div className="stat-card">
          <div className="sc-icon" style={{background:'#eef2ff'}}>🔍</div>
          <div className="sc-label">Filtered Total</div>
          <div className="sc-value" style={{fontSize:'.95rem'}}>{fmtMoney(totalFiltered)}</div>
          <div className="sc-sub">{expenses.length} records</div>
        </div>
        <div className="stat-card">
          <div className="sc-icon" style={{background:'#ecfdf5'}}>🏆</div>
          <div className="sc-label">Top Category</div>
          <div className="sc-value" style={{fontSize:summary?.categoryBreakdown?.[0]?._id?'.85rem':'.76rem',textTransform:'capitalize',color:summary?.categoryBreakdown?.[0]?._id?'#0f1123':'#b0b5cc'}}>{summary?.categoryBreakdown?.[0]?._id||'No data yet'}</div>
          <div className="sc-sub">{summary?.categoryBreakdown?.[0]?._id?fmtMoney(summary.categoryBreakdown[0].total):''}</div>
        </div>
      </div>

      {/* Charts */}
      {summary&&(
        <div className="charts-row">
          <div className="chart-panel">
            <div className="chart-title">Monthly Expenses</div>
            <div className="chart-sub">Last 6 months spending</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={summary.monthlyChart} barSize={20}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f7" vertical={false}/>
                <XAxis dataKey="label" tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontFamily:'Sora',fontSize:10,fill:'#9094a8'}} axisLine={false} tickLine={false} width={48} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}K`:v}/>
                <Tooltip content={<CustomTooltip/>}/>
                <Bar dataKey="total" name="Total" fill="#ef4444" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-panel">
            <div className="chart-title">By Category</div>
            <div className="chart-sub">This year</div>
            {pieData.length>0?(
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={2}>
                    {pieData.map((e,i)=><Cell key={i} fill={e.fill}/>)}
                  </Pie>
                  <Tooltip formatter={v=>fmtMoney(v)}/>
                  <Legend wrapperStyle={{fontFamily:'Sora',fontSize:10}}/>
                </PieChart>
              </ResponsiveContainer>
            ):(
              <div style={{height:200,display:'flex',alignItems:'center',justifyContent:'center',color:'#9094a8',fontSize:'.8rem'}}>No data yet</div>
            )}
          </div>
        </div>
      )}

      {/* Module tabs — only when Owner has enabled Library/Hostel for this school */}
      {showModuleTabs && (
        <div className="cat-tabs">
          <button className={`cat-tab${!filterModule?' active':''}`} onClick={()=>setFilterModule('')}>All</button>
          {hostelEnabled  && <button className={`cat-tab${filterModule==='hostel'?' active':''}`}  onClick={()=>setFilterModule('hostel')}>🏠 Hostel</button>}
          {libraryEnabled && <button className={`cat-tab${filterModule==='library'?' active':''}`} onClick={()=>setFilterModule('library')}>📚 Library</button>}
          <button className={`cat-tab${filterModule==='school'?' active':''}`} onClick={()=>setFilterModule('school')}>🏫 School</button>
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar">
        <FunnelIcon style={{width:15,height:15,stroke:'#9094a8',flexShrink:0}}/>
        <input type="month" className="filter-input" value={filterMonth} onChange={e=>setFilterMonth(e.target.value)}/>
        <select className="filter-select" value={filterCategory} onChange={e=>setFilterCategory(e.target.value)}>
          <option value="">All Categories</option>
          {sortAsc(CATEGORIES).map(c=><option key={c} value={c}>{CAT_ICONS[c]} {c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={()=>{setFilterMonth('');setFilterCategory('');}}>Reset</button>
      </div>

      {/* Table — desktop */}
      <div className="panel">
        <div className="panel-head">
          <div className="panel-title">Expense Records <span style={{fontWeight:400,color:'#9094a8',marginLeft:8,fontSize:'.76rem'}}>{expenses.length} records · {fmtMoney(totalFiltered)}</span></div>
        </div>
        {loading ? (
          <div className="ex-loading"><div className="spinner"/>Loading…</div>
        ) : expenses.length===0 ? (
          <div className="ex-empty">No expenses found. <button className="btn btn-blue btn-sm" style={{marginLeft:8}} onClick={openAdd}><PlusIcon/>Add First</button></div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="ex-table-wrap" style={{overflowX:'auto'}}>
              <table className="ex-table">
                <thead><tr>
                  <th>Date</th><th>Category</th><th>Sub-type</th><th>Title</th><th>Description</th><th>Vendor</th><th>Method</th><th>Amount</th><th>Actions</th>
                </tr></thead>
                <tbody>
                  {expenses.map(exp=>(
                    <tr key={exp._id}>
                      <td style={{color:'#6b7194',fontFamily:"'JetBrains Mono',monospace",fontSize:'.74rem',whiteSpace:'nowrap'}}>{fmtDate(exp.date)}</td>
                      <td>
                        <span className="cat-badge" style={{background:CAT_BG[exp.category]||'#f0f2f7',color:CAT_COLORS[exp.category]||'#6b7194'}}>
                          {CAT_ICONS[exp.category]} {exp.category}
                        </span>
                      </td>
                      <td>{exp.subcategory&&<span className="sub-badge">{exp.subcategory}</span>}</td>
                      <td style={{fontWeight:600,color:'#0f1123',maxWidth:180}}>
                        <div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{exp.title}</div>
                        {exp.unitsConsumed>0&&<div style={{fontSize:'.68rem',color:'#3b6ef8',marginTop:1}}>{exp.unitsConsumed} units</div>}
                      </td>
                      <td style={{fontSize:'.76rem',color:'#6b7194',maxWidth:160}}>
                        <div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={exp.description||''}>{exp.description||'—'}</div>
                      </td>
                      <td style={{fontSize:'.76rem',color:'#6b7194'}}>{exp.vendor||'—'}</td>
                      <td style={{fontSize:'.74rem',color:'#9094a8',textTransform:'capitalize'}}>{exp.paymentMethod}</td>
                      <td><span className="amount-cell">{fmtMoney(exp.amount)}</span></td>
                      <td>
                        <div style={{display:'flex',gap:6}}>
                          <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(exp)}><PencilSquareIcon/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="ex-cards-wrap" style={{padding:'12px'}}>
              {expenses.map(exp=>(
                <div key={exp._id} className="ex-card">
                  <div className="ec-top">
                    <div>
                      <div className="ec-title">{exp.title}</div>
                      <div className="ec-meta">{fmtDate(exp.date)}</div>
                    </div>
                    <span className="amount-cell" style={{fontSize:'1rem'}}>{fmtMoney(exp.amount)}</span>
                  </div>
                  <div className="ec-row">
                    <span className="ec-row-label">Category</span>
                    <span className="cat-badge" style={{background:CAT_BG[exp.category]||'#f0f2f7',color:CAT_COLORS[exp.category]||'#6b7194'}}>{CAT_ICONS[exp.category]} {exp.category}</span>
                  </div>
                  {exp.subcategory&&<div className="ec-row"><span className="ec-row-label">Sub-type</span><span className="sub-badge">{exp.subcategory}</span></div>}
                  {exp.description&&<div className="ec-row"><span className="ec-row-label">Description</span><span style={{textAlign:'right'}}>{exp.description}</span></div>}
                  {exp.vendor&&<div className="ec-row"><span className="ec-row-label">Vendor</span><span style={{fontWeight:600}}>{exp.vendor}</span></div>}
                  <div className="ec-row"><span className="ec-row-label">Payment</span><span style={{textTransform:'capitalize'}}>{exp.paymentMethod}</span></div>
                  <div className="ec-actions">
                    <button className="btn btn-ghost btn-sm" style={{flex:1,justifyContent:'center'}} onClick={()=>openEdit(exp)}><PencilSquareIcon/>Edit</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {modal&&createPortal(
        <div className="modal-backdrop" onClick={()=>setModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{editing?'✏️ Edit Expense':'➕ Add Expense'}</div>
              <button className="modal-close" onClick={()=>setModal(false)}><XMarkIcon/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">

                {/* Module */}
                {showModuleTabs && (
                  <div className="form-group">
                    <label className="form-label">Belongs To</label>
                    <select className="form-select" value={form.module} onChange={e=>setF('module',e.target.value)}>
                      <option value="school">🏫 School</option>
                      {hostelEnabled  && <option value="hostel">🏠 Hostel</option>}
                      {libraryEnabled && <option value="library">📚 Library</option>}
                    </select>
                  </div>
                )}

                {/* Category */}
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select className="form-select" value={form.category} onChange={e=>handleCatChange(e.target.value)}>
                    {CATEGORIES.map(c=><option key={c} value={c}>{CAT_ICONS[c]} {c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                  </select>
                </div>

                {/* Sub-category */}
                <div className="form-group">
                  <label className="form-label">Sub-type *</label>
                  <select className="form-select" value={form.subcategory} onChange={e=>setF('subcategory',e.target.value)}>
                    <option value="">— Select type —</option>
                    {subs.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                {/* Title */}
                <div className="form-group form-full">
                  <label className="form-label">Title</label>
                  <input className="form-input" value={form.title}
                    placeholder={form.subcategory ? `e.g. ${form.subcategory} - ${new Date().toLocaleDateString('en-US',{month:'long'})}` : 'e.g. Electricity Bill - March'}
                    onChange={e=>setF('title',e.target.value)}/>
                </div>

                {/* Description */}
                <div className="form-group form-full">
                  <label className="form-label">Description</label>
                  <textarea className="form-input" style={{minHeight:60,resize:'vertical'}} value={form.description}
                    placeholder="Optional additional details"
                    onChange={e=>setF('description',e.target.value)}/>
                </div>

                {/* Amount + Date */}
                <div className="form-group">
                  <label className="form-label">Amount (Rs.) *</label>
                  <input className="form-input" type="number" value={form.amount} placeholder="0" onChange={e=>setF('amount',e.target.value)}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input className="form-input" type="date" value={form.date} onChange={e=>setF('date',e.target.value)}/>
                </div>

                {/* Payment method + vendor */}
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <select className="form-select" value={form.paymentMethod} onChange={e=>setF('paymentMethod',e.target.value)}>
                    {['cash','bank','cheque','online'].map(m=><option key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Vendor / Paid To</label>
                  <input className="form-input" value={form.vendor} placeholder="e.g. LESCO, Shell, Ali Bros" onChange={e=>setF('vendor',e.target.value)}/>
                </div>

                <div className="form-group">
                  <label className="form-label">Receipt No.</label>
                  <input className="form-input" value={form.receiptNo} placeholder="Optional" onChange={e=>setF('receiptNo',e.target.value)}/>
                </div>

                {/* Meter readings — electricity / generator / water */}
                {['electricity','generator','water'].includes(form.category)&&<>
                  <div className="form-group">
                    <label className="form-label">Previous Reading</label>
                    <input className="form-input" type="number" value={form.previousReading} placeholder="Units" onChange={e=>setF('previousReading',e.target.value)}/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Current Reading</label>
                    <input className="form-input" type="number" value={form.meterReading} placeholder="Units" onChange={e=>setF('meterReading',e.target.value)}/>
                  </div>
                  {form.meterReading&&form.previousReading&&Number(form.meterReading)>Number(form.previousReading)&&(
                    <div className="form-group form-full">
                      <div className="info-box">⚡ Units consumed: {Number(form.meterReading)-Number(form.previousReading)}</div>
                    </div>
                  )}
                </>}
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleSave} disabled={saving}>{saving?'Saving…':editing?'Save Changes':'Add Expense'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

export default Expenses;